import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

const useSetting = () => {
  const { api, AdminId } = useGlobalContext();

  const [deliveryCharges, setDeliveryCharges] = useState([]);
  const [deliveryChargesLoading, setDeliveryChargesLoading] = useState(false);

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  // ================= GET DELIVERY CHARGES =================

  const fetchDeliveryCharges = useCallback(
    async (restaurantId = "") => {
      setDeliveryChargesLoading(true);

      try {
        const response = await api.post("/api/Admin/v1.0/getDeliveryCharges", {
          userid: String(restaurantId || "").trim(),
        });

        const data = response.data;

        if (data?.responseCode === "00") {
          const list = Array.isArray(data?.data) ? data.data : [];

          setDeliveryCharges(list);
          return list;
        }

        if (shouldShowLocalError(data)) {
          toast.error(
            data?.responseMessage || "Failed to fetch delivery charges",
          );
        }

        setDeliveryCharges([]);
        return [];
      } catch (error) {
        console.error("fetchDeliveryCharges error:", error);

        toast.error(
          error?.response?.data?.responseMessage ||
            "Error fetching delivery charges",
        );

        setDeliveryCharges([]);
        return [];
      } finally {
        setDeliveryChargesLoading(false);
      }
    },
    [api],
  );

  // ================= ADD DELIVERY CHARGES =================

  const addDeliveryCharges = async (form) => {
    const restaurantId = String(form?.userId || "").trim();
    const minimumDistanceValue = String(form?.minimumDistance ?? "").trim();
    const minimumOrderAmountValue = String(
      form?.minimumOrderAmount ?? "",
    ).trim();
    const deliveryChargesValue = String(form?.deliveryCharges ?? "").trim();
    const perKmChargesValue = String(form?.perKmCharges ?? "").trim();

    if (!AdminId) {
      toast.error("Admin ID is required");
      return false;
    }

    if (!restaurantId) {
      toast.error("Restaurant ID is required");
      return false;
    }

    if (restaurantId.length < 2) {
      toast.error("Please enter a valid Restaurant ID");
      return false;
    }

    if (!minimumDistanceValue) {
      toast.error("Minimum distance is required");
      return false;
    }

    if (!minimumOrderAmountValue) {
      toast.error("Minimum order amount is required");
      return false;
    }

    if (!deliveryChargesValue) {
      toast.error("Delivery charges are required");
      return false;
    }

    if (!perKmChargesValue) {
      toast.error("Per KM charges are required");
      return false;
    }

    const minimumDistance = Number(minimumDistanceValue);
    const minimumOrderAmount = Number(minimumOrderAmountValue);
    const deliveryChargeAmount = Number(deliveryChargesValue);
    const perKmCharges = Number(perKmChargesValue);

    if (!Number.isFinite(minimumDistance)) {
      toast.error("Please enter a valid minimum distance");
      return false;
    }

    if (minimumDistance < 0) {
      toast.error("Minimum distance cannot be negative");
      return false;
    }

    if (!Number.isFinite(minimumOrderAmount)) {
      toast.error("Please enter a valid minimum order amount");
      return false;
    }

    if (minimumOrderAmount < 0) {
      toast.error("Minimum order amount cannot be negative");
      return false;
    }

    if (!Number.isFinite(deliveryChargeAmount)) {
      toast.error("Please enter valid delivery charges");
      return false;
    }

    if (deliveryChargeAmount < 0) {
      toast.error("Delivery charges cannot be negative");
      return false;
    }

    if (!Number.isFinite(perKmCharges)) {
      toast.error("Please enter valid per KM charges");
      return false;
    }

    if (perKmCharges < 0) {
      toast.error("Per KM charges cannot be negative");
      return false;
    }

    const payload = {
      adminId: String(AdminId),
      userId: restaurantId,
      minimumDistance: String(minimumDistance),
      minimumOrderAmount: String(minimumOrderAmount),
      deliveryCharges: String(deliveryChargeAmount),
      perKmCharges: String(perKmCharges),
    };

    setDeliveryChargesLoading(true);

    try {
      const response = await api.post(
        "/api/Admin/v1.0/insertDeliveryCharges",
        payload,
      );

      const data = response.data;

      if (data?.responseCode === "00") {
        toast.success(
          data?.responseMessage || "Delivery charges inserted successfully",
        );

        await fetchDeliveryCharges("");
        return true;
      }

      if (shouldShowLocalError(data)) {
        toast.error(
          data?.responseMessage || "Failed to insert delivery charges",
        );
      }

      return false;
    } catch (error) {
      console.error("addDeliveryCharges error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error inserting delivery charges",
      );

      return false;
    } finally {
      setDeliveryChargesLoading(false);
    }
  };

  // ================= DELETE DELIVERY CHARGES =================

  const deleteDeliveryCharges = async (chargeId) => {
    if (!chargeId) {
      toast.error("Invalid charge ID");
      return false;
    }

    if (!AdminId) {
      toast.error("Admin ID is required");
      return false;
    }

    setDeliveryChargesLoading(true);

    try {
      const response = await api.post("/api/Admin/v1.0/deleteDeliveryCharges", {
        userid: String(AdminId),
        chargeId: String(chargeId),
      });

      const data = response.data;

      if (data?.responseCode === "00") {
        toast.success(
          data?.responseMessage || "Delivery charges deleted successfully",
        );

        await fetchDeliveryCharges("");
        return true;
      }

      if (shouldShowLocalError(data)) {
        toast.error(
          data?.responseMessage || "Failed to delete delivery charges",
        );
      }

      return false;
    } catch (error) {
      console.error("deleteDeliveryCharges error:", error);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error deleting delivery charges",
      );

      return false;
    } finally {
      setDeliveryChargesLoading(false);
    }
  };

  return {
    deliveryCharges,
    deliveryChargesLoading,
    fetchDeliveryCharges,
    addDeliveryCharges,
    deleteDeliveryCharges,
  };
};

export default useSetting;
